import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { areaNormalFontFamily } from './constants';

export type InputProps = Omit<TextFieldProps, 'size'> & {
    maxLength?: number;
    readOnly?: boolean;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    function Input({ maxLength, readOnly, label, placeholder, ...props }, ref) {
        return (
            <TextField
                {...props}
                variant="outlined"
                fullWidth
                label={label}
                placeholder={label ? undefined : placeholder}
                slotProps={{
                    ...props.slotProps,
                    htmlInput: {
                        ...props.slotProps?.htmlInput,
                        ref,
                        maxLength,
                        readOnly,
                    },
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: 56,
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-input': {
                            fontFamily: areaNormalFontFamily,
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#282636',
                            caretColor: '#715AFF',
                            // Top padding creates room for the floating label inside the box
                            padding: label ? '27px 14px 8px' : '0 14px',
                            height: '100%',
                            boxSizing: 'border-box',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D0CFD9',
                            borderWidth: '1px',
                            borderRadius: '8px',
                            transition:
                                'border-color 0.15s ease, box-shadow 0.15s ease',
                        },
                        // Suppress the border notch — label stays inside the box, not on the border
                        '& .MuiOutlinedInput-notchedOutline legend': {
                            maxWidth: '0px !important',
                            padding: 0,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D0CFD9',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#715AFF',
                            borderWidth: '1px',
                            boxShadow: '0 0 0 1px #715AFF',
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E3291B',
                        },
                        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
                            {
                                borderColor: '#E3291B',
                                boxShadow: '0 0 0 1px #E3291B',
                            },
                        '&.Mui-disabled': {
                            backgroundColor: '#F3F3F7',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#D0CFD9',
                            },
                        },
                        '&.Mui-disabled .MuiOutlinedInput-input': {
                            cursor: 'not-allowed',
                            // Override browser's default disabled text colour
                            WebkitTextFillColor: '#282636',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        fontFamily: areaNormalFontFamily,
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#73717F',
                        lineHeight: '24px',
                        // Vertically centre inside the 56 px container (16 px from top of FormControl)
                        transform: 'translate(14px, 16px) scale(1)',
                        transition:
                            'color 0.15s ease, transform 0.15s ease, font-size 0.15s ease, letter-spacing 0.15s ease',
                        pointerEvents: 'none',
                        '&.Mui-focused': {
                            color: '#715AFF',
                        },
                        '&.Mui-error': {
                            color: '#E3291B',
                        },
                        // Floated: keep label inside the box at 9 px from top (no border-line trick)
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, 9px) scale(1)',
                            fontSize: '14px',
                            lineHeight: '14px',
                            letterSpacing: '0.25px',
                        },
                        '&.Mui-disabled': {
                            color: '#73717F',
                        },
                    },
                    '& .MuiFormHelperText-root': {
                        fontFamily: areaNormalFontFamily,
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: '#73717F',
                        margin: '4px 0 0',
                        '&.Mui-error': {
                            color: '#E3291B',
                        },
                    },
                }}
            />
        );
    },
);

Input.displayName = 'Input';
